import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Dropdown from "@/components/ui/dropdown";
import Button from "@/components/ui/button";
import Switch from "@/components/ui/switch";
import {
  LeftChevron,
  Eye,
  Clock,
  ArrowRightLong,
  Edit,
  Exam,
  Check,
  Delete,
  Plus,
  Close,
  Pencil,
  ArrowLeftLong,
} from "@/components/icons";
import FileInput from "@/components/ui/file-input";
import ExamQuestionPreview from "../components/ExamQuestionPreview";
import type { ExamPreviewQuestion } from "../components/ExamQuestionPreview";
import { examManagementApi } from "../services/exam-management.api";
import type { CreateExamDto, ExamResDto } from "../types/exam-management.types";
import { ALLOWED_FILE_TYPE_PRESETS } from "@/types/file-upload.types";
import styles from "./CreateExamPage.module.scss";

const STEPS = [
  { key: "basic", labelKey: "createExam.steps.basicInfo", icon: Pencil },
  {
    key: "questions",
    labelKey: "createExam.steps.examQuestions",
    icon: Exam,
  },
  { key: "preview", labelKey: "createExam.steps.preview", icon: Eye },
] as const;

interface BasicInfoFormData {
  examName: string;
  examDescription: string;
  passingScore: string;
  duration: string;
  durationUnit: string;
  canGoPreviousQuestions: boolean;
}

const durationUnitOptions = [
  { label: "Dakika", value: "dakika" },
  { label: "Saat", value: "saat" },
];

interface ExamQuestionOption {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface ExamQuestion {
  id: string;
  text: string;
  options: ExamQuestionOption[];
  allowMultiple: boolean;
  imageFileID?: string;
  imagePreview?: string;
}

const createEmptyOption = (): ExamQuestionOption => ({
  id: crypto.randomUUID(),
  text: "",
  isCorrect: false,
});

const createEmptyQuestion = (): ExamQuestion => ({
  id: crypto.randomUUID(),
  text: "",
  options: [
    createEmptyOption(),
    createEmptyOption(),
    createEmptyOption(),
    createEmptyOption(),
  ],
  allowMultiple: false,
});

/** Zod: Temel bilgiler (sınav adı, geçme notu, sınav süresi) – CreateExamDto kurallarına uygun */
const createExamBasicInfoSchema = z
  .object({
    examName: z
      .string()
      .min(1, "createExam.validation.nameRequired")
      .max(255, "createExam.validation.nameMaxLength"),
    examDescription: z
      .string()
      .max(2000, "createExam.validation.descriptionMaxLength")
      .optional(),
    passingScore: z
      .string()
      .min(1, "createExam.validation.passingGradeRequired")
      .refine(
        (val) => {
          const n = parseInt(val, 10);
          return !Number.isNaN(n) && n >= 1 && n <= 100;
        },
        { message: "createExam.validation.passingGradeRange" },
      ),
    duration: z.string().min(1, "createExam.validation.durationRequired"),
    durationUnit: z.enum(["dakika", "saat"]),
  })
  .refine(
    (data) => {
      const num = parseInt(data.duration, 10);
      if (Number.isNaN(num) || num < 1) return false;
      const durationMinutes = data.durationUnit === "saat" ? num * 60 : num;
      return durationMinutes >= 1 && durationMinutes <= 360;
    },
    {
      message: "createExam.validation.durationRange",
      path: ["duration"],
    },
  );

function validateCreateExamForm(
  formData: BasicInfoFormData,
  questions: ExamQuestion[],
  t: TFunction,
):
  | { success: true }
  | { success: false; errors: string[]; fieldErrors: Record<string, string> } {
  const basicResult = createExamBasicInfoSchema.safeParse({
    examName: formData.examName,
    examDescription: formData.examDescription || undefined,
    passingScore: formData.passingScore,
    duration: formData.duration,
    durationUnit: formData.durationUnit as "dakika" | "saat",
  });

  if (!basicResult.success) {
    const errors = basicResult.error.flatten();
    const messages: string[] = [
      ...(errors.formErrors ?? []),
      ...Object.values(errors.fieldErrors)
        .flat()
        .filter((m): m is string => typeof m === "string"),
    ];
    const fieldErrors: Record<string, string> = {};
    Object.entries(errors.fieldErrors ?? {}).forEach(([key, val]) => {
      const first = Array.isArray(val) ? val[0] : val;
      if (typeof first === "string") fieldErrors[key] = first;
    });
    return { success: false, errors: messages, fieldErrors };
  }

  const nonEmptyQuestions = questions.filter((q) => q.text.trim().length > 0);
  if (nonEmptyQuestions.length === 0) {
    return {
      success: false,
      errors: [
        t("createExam.validation.questionsRequired"),
      ],
      fieldErrors: {},
    };
  }

  // Her doldurulmuş soruda en az 2 dolu seçenek + en az 1 işaretli doğru cevap
  // şartını ARANIYORSA bu kontrol soruları silently filter etmek yerine yayın
  // akışını bloklamalı — aksi halde yanlış kurgulu sınav yayına çıkabiliyor.
  for (let i = 0; i < nonEmptyQuestions.length; i++) {
    const q = nonEmptyQuestions[i];
    const optionsWithText = q.options.filter((opt) => opt.text.trim() !== "");
    if (optionsWithText.length < 2) {
      return {
        success: false,
        errors: [
          t("createExam.validation.questionNeedsTwoOptions", { n: i + 1 }),
        ],
        fieldErrors: {},
      };
    }
    if (!optionsWithText.some((opt) => opt.isCorrect)) {
      return {
        success: false,
        errors: [
          t("createExam.validation.questionNeedsCorrect", { n: i + 1 }),
        ],
        fieldErrors: {},
      };
    }
  }

  return { success: true };
}

/** Sadece Temel Bilgiler adımını doğrular (İleri butonu için, sorular kontrol edilmez) */
function validateBasicInfoStep(
  formData: BasicInfoFormData,
):
  | { success: true }
  | { success: false; errors: string[]; fieldErrors: Record<string, string> } {
  const result = createExamBasicInfoSchema.safeParse({
    examName: formData.examName,
    examDescription: formData.examDescription || undefined,
    passingScore: formData.passingScore,
    duration: formData.duration,
    durationUnit: formData.durationUnit as "dakika" | "saat",
  });
  if (result.success) return { success: true };
  const errors = result.error.flatten();
  const messages: string[] = [
    ...(errors.formErrors ?? []),
    ...Object.values(errors.fieldErrors)
      .flat()
      .filter((m): m is string => typeof m === "string"),
  ];
  const fieldErrors: Record<string, string> = {};
  Object.entries(errors.fieldErrors ?? {}).forEach(([key, val]) => {
    const first = Array.isArray(val) ? val[0] : val;
    if (typeof first === "string") fieldErrors[key] = first;
  });
  return { success: false, errors: messages, fieldErrors };
}

/** Frontend form -> Backend CreateExamDto payload */
function buildCreateExamPayload(
  formData: BasicInfoFormData,
  questions: ExamQuestion[],
  status: number,
): CreateExamDto {
  const passingScore = Math.max(
    1,
    Math.min(100, parseInt(formData.passingScore, 10) || 70),
  );

  const durationNum = parseInt(formData.duration, 10) || 30;
  const durationMinutes =
    formData.durationUnit === "saat" ? durationNum * 60 : durationNum;
  const clampedDuration = Math.min(360, Math.max(1, durationMinutes));

  const validQuestions = questions
    .map((q, index) => {
      const optionsWithText = q.options
        .filter((opt) => opt.text.trim() !== "")
        .slice(0, 5);
      if (optionsWithText.length < 2 || !q.text.trim()) return null;
      const hasCorrect = optionsWithText.some((opt) => opt.isCorrect);
      if (!hasCorrect) return null;

      return {
        questionText: q.text.trim(),
        order: index,
        allowMultipleSelection: q.allowMultiple,
        imageFileID: q.imageFileID,
        options: optionsWithText.map((opt, optIndex) => ({
          optionText: opt.text.trim(),
          order: optIndex,
          isCorrect: opt.isCorrect,
        })),
      };
    })
    .filter((q): q is NonNullable<typeof q> => q !== null);

  return {
    name: formData.examName.trim(),
    description: formData.examDescription.trim() || undefined,
    passingScore,
    durationMinutes: clampedDuration,
    status,
    questions: validQuestions,
    canGoPreviousQuestions: formData.canGoPreviousQuestions,
  };
}

function GripVerticalIcon({
  width = 20,
  height = 20,
}: {
  width?: number;
  height?: number;
}) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="10" cy="6" r="1.25" fill="currentColor" />
      <circle cx="10" cy="10" r="1.25" fill="currentColor" />
      <circle cx="10" cy="14" r="1.25" fill="currentColor" />
    </svg>
  );
}

interface CreateExamPageProps {
  examId?: string;
}

const CreateExamPage: React.FC<CreateExamPageProps> = ({ examId }) => {
  const isEditMode = !!examId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<BasicInfoFormData>({
    examName: "",
    examDescription: "",
    passingScore: "",
    duration: "",
    durationUnit: "saat",
    canGoPreviousQuestions: true,
  });
  const [questions, setQuestions] = useState<ExamQuestion[]>([
    createEmptyQuestion(),
  ]);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoadingExam, setIsLoadingExam] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Backend'den yüklenen (kaydedilmiş) soru ID'leri. Reorder endpoint'i
  // tüm aktif soruların ID'sini istediği için, yalnızca server'da var olan
  // kümeyle çağrı yapabiliyoruz.
  const serverQuestionIdsRef = useRef<Set<string>>(new Set());

  /** Edit modunda mevcut sınavı yükle */
  const loadExam = useCallback(
    async (id: string) => {
      setIsLoadingExam(true);
      try {
        const { data: responseData } = await examManagementApi.getRaw(id);
        const item: ExamResDto = responseData;
        setFormData({
          examName: item.name ?? "",
          examDescription: item.description ?? "",
          passingScore: String(item.passingScore ?? ""),
          duration: String(item.durationMinutes ?? ""),
          durationUnit: "dakika",
          canGoPreviousQuestions: item.canGoPreviousQuestions ?? true,
        });
        setFieldErrors({});
        if (item.questions?.length) {
          const mapped: ExamQuestion[] = item.questions
            .sort((a, b) => a.order - b.order)
            .map((q) => ({
              id: q.id,
              text: q.questionText,
              options: q.options
                .sort((a, b) => a.order - b.order)
                .map((opt) => ({
                  id: opt.id,
                  text: opt.optionText,
                  isCorrect: opt.isCorrect,
                })),
              allowMultiple: q.allowMultipleSelection,
              imageFileID: q.imageFileID ?? undefined,
              imagePreview: q.image?.downloadUrl ?? q.imageUrl ?? undefined,
            }));
          setQuestions(mapped);
          serverQuestionIdsRef.current = new Set(mapped.map((q) => q.id));
        }
      } catch {
        toast.error(t("editExam.loadError"));
        navigate("/dashboard/exam-management");
      } finally {
        setIsLoadingExam(false);
      }
    },
    [navigate, t],
  );

  useEffect(() => {
    if (examId) {
      loadExam(examId);
    }
  }, [examId, loadExam]);

  const handleGoBack = () => {
    navigate("/dashboard/exam-management");
  };

  const breadcrumbItems = [
    {
      label: t("createExam.breadcrumb.exams"),
      onClick: () => navigate("/dashboard/exam-management"),
    },
    {
      label: isEditMode
        ? t("editExam.breadcrumb.editExam")
        : t("createExam.breadcrumb.createExam"),
    },
  ];

  const handleInputChange = (field: keyof BasicInfoFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      if (currentStep === 0) {
        const validation = validateBasicInfoStep(formData);
        if (!validation.success) {
          const translatedFieldErrors: Record<string, string> = {};
          Object.entries(validation.fieldErrors).forEach(([key, val]) => {
            translatedFieldErrors[key] = t(val);
          });
          setFieldErrors(translatedFieldErrors);
          toast.error(
            (validation.errors[0] && t(validation.errors[0])) ??
              t("createExam.validation.invalid"),
          );
          return;
        }
        setFieldErrors({});
      }
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSaveDraft = async () => {
    const validation = validateCreateExamForm(formData, questions, t);
    if (!validation.success) {
      const translatedFieldErrors: Record<string, string> = {};
      Object.entries(validation.fieldErrors).forEach(([key, val]) => {
        translatedFieldErrors[key] = t(val);
      });
      setFieldErrors(translatedFieldErrors);
      toast.error(
        (validation.errors[0] && t(validation.errors[0])) ??
          t("createExam.validation.invalid"),
      );
      return;
    }
    setFieldErrors({});
    setIsSavingDraft(true);
    try {
      const payload = buildCreateExamPayload(formData, questions, 0);
      if (isEditMode && examId) {
        await examManagementApi.update(
          examId,
          payload as unknown as Record<string, unknown>,
        );
        toast.success(
          t("editExam.draftSuccess"),
        );
      } else {
        await examManagementApi.create(payload);
        toast.success(
          t("createExam.draftSuccess"),
        );
      }
      navigate("/dashboard/exam-management");
    } catch {
      toast.error(
        t("createExam.draftError"),
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    const validation = validateCreateExamForm(formData, questions, t);
    if (!validation.success) {
      const translatedFieldErrors: Record<string, string> = {};
      Object.entries(validation.fieldErrors).forEach(([key, val]) => {
        translatedFieldErrors[key] = t(val);
      });
      setFieldErrors(translatedFieldErrors);
      toast.error(
        (validation.errors[0] && t(validation.errors[0])) ??
          t("createExam.validation.invalid"),
      );
      return;
    }
    setFieldErrors({});
    setIsPublishing(true);
    try {
      const payload = buildCreateExamPayload(formData, questions, 1);
      if (isEditMode && examId) {
        await examManagementApi.update(
          examId,
          payload as unknown as Record<string, unknown>,
        );
        toast.success(
          t("editExam.publishSuccess"),
        );
      } else {
        await examManagementApi.create(payload);
        toast.success(
          t("createExam.publishSuccess"),
        );
      }
      navigate("/dashboard/exam-management", {
        state: { examCreatedSuccess: true },
      });
    } catch {
      toast.error(
        t("createExam.publishError"),
      );
    } finally {
      setIsPublishing(false);
    }
  };

  const selectedQuestion = questions[selectedQuestionIndex];

  const handleAddQuestion = () => {
    const newQuestion = createEmptyQuestion();
    setQuestions((prev) => [...prev, newQuestion]);
    setSelectedQuestionIndex(questions.length);
  };

  const handleDeleteQuestion = (index: number) => {
    if (questions.length <= 1) return;
    setQuestions((prev) => prev.filter((_, i) => i !== index));
    setSelectedQuestionIndex((prev) => Math.min(prev, Math.max(0, index - 1)));
  };

  const handleQuestionDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleQuestionDragOver = (
    index: number,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleQuestionDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleQuestionDrop = async (
    targetIndex: number,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (fromIndex === null || fromIndex === targetIndex) return;

    const reordered = [...questions];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setQuestions(reordered);

    // Seçili soru indexini takip et (sürüklenen soru taşındıysa onu seçili tut)
    setSelectedQuestionIndex((prev) => {
      if (prev === fromIndex) return targetIndex;
      if (fromIndex < prev && targetIndex >= prev) return prev - 1;
      if (fromIndex > prev && targetIndex <= prev) return prev + 1;
      return prev;
    });

    // Yalnızca edit modunda (examId var) ve tüm sorular server'da kayıtlıysa
    // backend'e reorder isteği gönder. Yeni eklenmiş (henüz kaydedilmemiş)
    // sorular varsa sıralama 'Güncelle' butonuyla kaydedilecek.
    if (!examId) return;
    const allOnServer = reordered.every((q) =>
      serverQuestionIdsRef.current.has(q.id),
    );
    if (!allOnServer) return;
    try {
      await examManagementApi.reorderQuestions(
        examId,
        reordered.map((q) => q.id),
      );
    } catch (err: unknown) {
      const statusCode = (err as { response?: { status?: number } })?.response
        ?.status;
      const errorCode = (
        err as {
          response?: { data?: { code?: string; errorCode?: string } };
        }
      )?.response?.data;
      const code = errorCode?.code ?? errorCode?.errorCode;
      if (statusCode === 404) {
        toast.error(t("createExam.questions.reorderNotFound"));
      } else if (
        statusCode === 400 &&
        code === "EXAM_QUESTIONS_REORDER_MISMATCH"
      ) {
        toast.error(
          t("createExam.questions.reorderMismatch"),
        );
      } else {
        toast.error(
          t("createExam.questions.reorderError"),
        );
      }
    }
  };

  const handleQuestionTextChange = (value: string) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === selectedQuestionIndex ? { ...q, text: value } : q,
      ),
    );
  };

  const handleAllowMultipleChange = (checked: boolean) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === selectedQuestionIndex ? { ...q, allowMultiple: checked } : q,
      ),
    );
  };

  const handleOptionChange = (optionId: string, text: string) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === selectedQuestionIndex
          ? {
              ...q,
              options: q.options.map((opt) =>
                opt.id === optionId ? { ...opt, text } : opt,
              ),
            }
          : q,
      ),
    );
  };

  const handleSetCorrectOption = (optionId: string) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== selectedQuestionIndex) return q;
        const allowMultiple = q.allowMultiple;
        return {
          ...q,
          options: q.options.map((opt) => ({
            ...opt,
            isCorrect: allowMultiple
              ? opt.id === optionId
                ? !opt.isCorrect
                : opt.isCorrect
              : opt.id === optionId,
          })),
        };
      }),
    );
  };

  const handleAddOption = () => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === selectedQuestionIndex
          ? { ...q, options: [...q.options, createEmptyOption()] }
          : q,
      ),
    );
  };

  const handleRemoveOption = (optionId: string) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== selectedQuestionIndex) return q;
        const filtered = q.options.filter((o) => o.id !== optionId);
        if (filtered.length < 2) return q;
        return { ...q, options: filtered };
      }),
    );
  };

  const handleImageUploadSuccess = (fileId: string, previewUrl?: string) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === selectedQuestionIndex
          ? { ...q, imageFileID: fileId, imagePreview: previewUrl }
          : q,
      ),
    );
  };

  const handleImageRemove = () => {
    const prev = selectedQuestion.imagePreview;
    if (prev?.startsWith("blob:")) {
      URL.revokeObjectURL(prev);
    }
    setQuestions((p) =>
      p.map((q, i) =>
        i === selectedQuestionIndex
          ? { ...q, imageFileID: undefined, imagePreview: undefined }
          : q,
      ),
    );
  };

  const handlePreviewPrevQuestion = () => {
    setSelectedQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const handlePreviewNextQuestion = () => {
    setSelectedQuestionIndex((prev) =>
      Math.min(questions.length - 1, prev + 1),
    );
  };

  if (isLoadingExam) {
    return (
      <DashboardLayout>
        <Header title={t("examManagement.title")} />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
            color: "#707a8f",
          }}
        >
          {t("common.loading")}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title={t("examManagement.title")} />

      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <div className={styles.breadcrumbContainer}>
            <div
              role="button"
              tabIndex={0}
              className={styles.backButton}
              onClick={handleGoBack}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleGoBack();
                }
              }}
              aria-label={t("common.back")}
            >
              <LeftChevron width={20} height={20} />
            </div>
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className={styles.stepperContainer}>
          {currentStep > 0 && (
            <Button
              type="button"
              variant="text"
              color="secondary"
              className={styles.inlineBackButton}
              startIcon={<ArrowLeftLong width={20} height={20} />}
              onClick={handlePrev}
            >
              {t("common.back")}
            </Button>
          )}
          <div className={styles.stepperRow}>
            <div className={styles.stepper}>
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === currentStep;
                const isChecked = index < currentStep;
                const connectorStatus =
                  index < STEPS.length - 1
                    ? currentStep > index
                      ? "completed"
                      : currentStep === index
                        ? "current"
                        : "future"
                    : null;
                return (
                  <React.Fragment key={step.key}>
                    <div
                      role="button"
                      tabIndex={0}
                      className={`${styles.step} ${isActive ? styles.stepActive : ""} ${isChecked ? styles.stepChecked : ""}`}
                      onClick={() => setCurrentStep(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setCurrentStep(index);
                        }
                      }}
                    >
                      <span className={styles.stepIcon}>
                        {isChecked ? (
                          <Check width={24} height={24} color="#fff" />
                        ) : (
                          <Icon width={24} height={24} />
                        )}
                      </span>
                      <span className={styles.stepLabel}>
                        {t(step.labelKey)}
                      </span>
                    </div>
                    {connectorStatus && (
                      <div className={styles.stepConnectorWrapper} aria-hidden>
                        <div
                          className={`${styles.stepConnector} ${connectorStatus === "completed" ? styles.stepConnectorCompleted : connectorStatus === "current" ? styles.stepConnectorCurrent : ""}`}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className={styles.nextButtonWrap}>
            {currentStep < STEPS.length - 1 && (
              <Button
                type="button"
                variant="text"
                color="primary"
                endIcon={<ArrowRightLong width={20} height={20} />}
                onClick={handleNext}
              >
                {t("createExam.next")}
              </Button>
            )}
            {currentStep === STEPS.length - 1 && (
              <div className={styles.actionButtons}>
                <Button
                  variant="soft"
                  color="primary"
                  className={styles.draftButton}
                  onClick={handleSaveDraft}
                  disabled={isSavingDraft || isPublishing}
                >
                  {t("createExam.actions.draft")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={styles.publishButton}
                  onClick={handlePublish}
                  disabled={isSavingDraft || isPublishing}
                >
                  {t("createExam.actions.publish")}
                </Button>
              </div>
            )}
          </div>
        </div>
        {currentStep === 0 && (
          <div className={styles.formCard}>
            <h2 className={styles.cardTitle}>
              {t("createExam.basicInfo.title")}
            </h2>
            <p className={styles.cardDescription}>
              {t("createExam.basicInfo.description")}
            </p>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <Input
                  label={t("createExam.basicInfo.examName")}
                  placeholder={t("createExam.basicInfo.examNamePlaceholder")}
                  value={formData.examName}
                  maxLength={255}
                  onChange={(e) =>
                    handleInputChange("examName", e.target.value)
                  }
                  error={!!fieldErrors.examName}
                  errorMessage={fieldErrors.examName}
                />
              </div>
              <div className={styles.formField}>
                <Textarea
                  label={t("createExam.basicInfo.examDescription")}
                  placeholder={t("createExam.basicInfo.examDescriptionPlaceholder")}
                  value={formData.examDescription}
                  maxLength={2000}
                  onChange={(e) =>
                    handleInputChange("examDescription", e.target.value)
                  }
                />
              </div>
              <div className={styles.formField}>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  label={t("createExam.basicInfo.passingScore")}
                  placeholder={t("createExam.basicInfo.passingScorePlaceholder")}
                  value={formData.passingScore}
                  onChange={(e) =>
                    handleInputChange("passingScore", e.target.value)
                  }
                  startIcon={<span className={styles.inputPrefix}>%</span>}
                  error={!!fieldErrors.passingScore}
                  errorMessage={fieldErrors.passingScore}
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formField}>
                  <Input
                    type="number"
                    min={1}
                    max={formData.durationUnit === "saat" ? 6 : 360}
                    label={t("createExam.basicInfo.duration")}
                    placeholder={t("createExam.basicInfo.durationPlaceholder")}
                    value={formData.duration}
                    onChange={(e) =>
                      handleInputChange("duration", e.target.value)
                    }
                    startIcon={<Clock width={20} height={20} />}
                    error={!!fieldErrors.duration}
                    errorMessage={fieldErrors.duration}
                  />
                </div>
                <div className={styles.durationDropdown}>
                  <Dropdown
                    placeholder={t("createExam.basicInfo.durationUnit")}
                    options={durationUnitOptions}
                    value={formData.durationUnit}
                    selectedLabel={
                      durationUnitOptions.find(
                        (o) => o.value === formData.durationUnit,
                      )?.label ?? "saat"
                    }
                    onSelect={(value) => {
                      const unit =
                        typeof value === "string" ? value : (value[0] ?? "");
                      handleInputChange("durationUnit", unit);
                    }}
                  />
                </div>
              </div>
              <div className={styles.formField}>
                <label className={styles.switchLabel}>
                  {t("createExam.basicInfo.canGoPreviousQuestions")}
                </label>
                <div className={styles.switchControl}>
                  <Switch
                    checked={formData.canGoPreviousQuestions}
                    onChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        canGoPreviousQuestions: checked,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <div className={styles.questionsStep}>
            <div className={styles.questionsColumns}>
              <div className={styles.questionsListColumn}>
                <div className={styles.questionsListCard}>
                  {questions.map((q, index) => (
                    <div
                      key={q.id}
                      role="button"
                      tabIndex={0}
                      draggable
                      className={`${styles.questionListItem} ${index === selectedQuestionIndex ? styles.questionListItemActive : ""} ${draggedIndex === index ? styles.questionListItemDragging : ""} ${dragOverIndex === index ? styles.questionListItemDragOver : ""}`}
                      onClick={() => setSelectedQuestionIndex(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedQuestionIndex(index);
                        }
                      }}
                      onDragStart={() => handleQuestionDragStart(index)}
                      onDragOver={(e) => handleQuestionDragOver(index, e)}
                      onDragEnd={handleQuestionDragEnd}
                      onDrop={(e) => handleQuestionDrop(index, e)}
                    >
                      <span
                        className={styles.questionListDrag}
                        aria-label={t("createExam.questions.dragHandle")}
                        title={t("createExam.questions.dragHandle")}
                      >
                        <GripVerticalIcon width={16} height={16} />
                      </span>
                      <div className={styles.questionListContent}>
                        <span className={styles.questionListLabel}>
                          {index + 1}.{" "}
                          {t("createExam.questions.questionShort")}
                        </span>
                        {q.text ? (
                          <p className={styles.questionListText}>{q.text}</p>
                        ) : null}
                      </div>
                      <span className={styles.questionListEdit} aria-hidden>
                        <Edit width={16} height={16} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.questionEditorColumn}>
                {selectedQuestion && (
                  <div className={styles.questionEditorCard}>
                    <div className={styles.questionEditorHeader}>
                      <h3 className={styles.questionEditorTitle}>
                        {selectedQuestionIndex + 1}.{" "}
                        {t("createExam.questions.questionShort")}
                      </h3>
                      <button
                        type="button"
                        className={styles.questionDeleteBtn}
                        onClick={() =>
                          handleDeleteQuestion(selectedQuestionIndex)
                        }
                        disabled={questions.length <= 1}
                        aria-label={t("common.delete")}
                      >
                        <Delete width={20} height={20} />
                      </button>
                    </div>
                    <div className={styles.questionEditorBody}>
                      <div className={styles.questionField}>
                        <Textarea
                          placeholder={t("createExam.questions.questionPlaceholder")}
                          label={t("createExam.questions.questionLabel")}
                          value={selectedQuestion.text}
                          onChange={(e) =>
                            handleQuestionTextChange(e.target.value)
                          }
                        />
                      </div>
                      <FileInput
                        key={selectedQuestionIndex}
                        label={t("createExam.questions.addImage")}
                        placeholder={t("createExam.fileModal.dropzoneText")}
                        accept={ALLOWED_FILE_TYPE_PRESETS.IMAGE.map(
                          (f) => `.${f.toLowerCase()}`,
                        ).join(",")}
                        supportedFormats={ALLOWED_FILE_TYPE_PRESETS.IMAGE}
                        maxSize={10}
                        maxFiles={1}
                        fileType="image"
                        uploadToBackend
                        onUploadSuccess={handleImageUploadSuccess}
                        onFileRemove={() => handleImageRemove()}
                        existingFileUrl={selectedQuestion.imagePreview}
                        enableDemoProgress={false}
                      />
                      <label className={styles.checkboxRow}>
                        <input
                          type="checkbox"
                          checked={selectedQuestion.allowMultiple}
                          onChange={(e) =>
                            handleAllowMultipleChange(e.target.checked)
                          }
                        />
                        <span>
                          {t("createExam.questions.allowMultiple")}
                        </span>
                      </label>
                      <p className={styles.correctAnswerHint}>
                        {t("createExam.questions.correctAnswerHint")}
                      </p>
                      <div className={styles.answerOptions}>
                        {selectedQuestion.options.map((opt) => (
                          <div
                            key={opt.id}
                            className={`${styles.answerOptionRow} ${opt.isCorrect ? styles.answerOptionRowSelected : ""}`}
                          >
                            <button
                              type="button"
                              className={styles.answerRadio}
                              onClick={() => handleSetCorrectOption(opt.id)}
                              aria-pressed={opt.isCorrect}
                              aria-label={t("createExam.questions.markCorrect")}
                            >
                              {opt.isCorrect && (
                                <Check width={14} height={14} color="#15AC77" />
                              )}
                            </button>
                            <div className={styles.answerOptionInputWrap}>
                              <Input
                                placeholder={t("createExam.questions.answerPlaceholder")}
                                value={opt.text}
                                onChange={(e) =>
                                  handleOptionChange(opt.id, e.target.value)
                                }
                              />
                            </div>
                            <button
                              type="button"
                              className={styles.removeOptionBtn}
                              onClick={() => handleRemoveOption(opt.id)}
                              disabled={selectedQuestion.options.length <= 2}
                              aria-label={t("common.delete")}
                            >
                              <Close
                                width={18}
                                height={18}
                                color="#707a8f"
                                border={false}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className={styles.addOptionLink}
                        onClick={handleAddOption}
                      >
                        + {t("createExam.questions.addOption")}
                      </button>
                      <button
                        type="button"
                        className={styles.addQuestionBtn}
                        onClick={handleAddQuestion}
                      >
                        <Plus width={20} height={20} color="#707A8F" />
                        {t("createExam.questions.addQuestion")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <ExamQuestionPreview
            questions={questions.map(
              (q): ExamPreviewQuestion => ({
                id: q.id,
                text: q.text,
                options: q.options.map((o) => ({
                  id: o.id,
                  text: o.text,
                  isCorrect: o.isCorrect,
                })),
                allowMultiple: q.allowMultiple,
                imagePreview: q.imagePreview,
              }),
            )}
            selectedQuestionIndex={selectedQuestionIndex}
            onSelectQuestion={setSelectedQuestionIndex}
            onPrev={handlePreviewPrevQuestion}
            onNext={handlePreviewNextQuestion}
            timerDisplay={{
              hours: "00",
              minutes: formData.duration?.trim() || "30",
              seconds: "00",
            }}
            mode="preview"
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default CreateExamPage;
